<template>
	<main>
		<div class="container c_inner">
			<section class="section_header">
				<div class="header_container">
					<h3 class="header_title">{{ getKnowhowDetailData?.knowhow.knowhow_title }}</h3>
					<div class="header_sub">
						<div class="sub_info">
							<span class="user">{{ getKnowhowDetailData?.knowhow.member_nickname }}</span
							><span class="view">조회수{{ getKnowhowDetailData?.knowhow.knowhow_hitcount }}</span>
							<span class="date">{{ getKnowhowDetailData?.knowhow.knowhow_enrollment_date.split(' ')[0] }}</span>
						</div>
						<IconLike
							:type="!myLike ? 'stroke' : 'fill'"
							class="sub_likeBtn"
							alt="좋아요"
							@click="onClickLike(Number(route.params.pk))"
						/>
					</div>
				</div>
			</section>
			<section class="section_body">
				<div class="body_box">
					<div class="box_content c_editorContent">
						<div v-dompurify-html="getKnowhowDetailData?.knowhow.knowhow_content" class="c_editorContent"></div>
					</div>
				</div>
				<div v-if="getKnowhowDetailData?.is_mine" class="body_btn">
					<button type="button" class="btn_delete" @click="onClickDeleteKnowhow(Number(route.params.pk))">
						게시글 삭제
					</button>
					<button type="button" class="btn_modify" @click="onClickEditKnowhow">게시글 수정</button>
				</div>
			</section>
			<Comments :comments="commentData" :counts="commentCntData" :board="'knowhow'" />
		</div>
	</main>
	<Modal v-if="route.query.modal === 'open'" modal-name="PostCreation" title="게시글 수정">
		<PostCreation :data="getKnowhowDetailData?.knowhow" />
	</Modal>
</template>

<script setup lang="ts">
// 내부 라이브러리
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { CommentWithReplies } from '@/api/comment';
// api
import { useAdjustLikesApi, useDeleteKnowhowApi, useGetKnowhowDetailApi } from '@/api/proTips';
import IconLike from '@/components/atom/icon/IconLike.vue';
// component
import Modal from '@/components/modal/Modal.vue';
import Comments from '@/components/postDetail/Comments.vue';
import PostCreation from '@/components/proTips/PostCreation.vue';
// constant
import { COMPANY_TITLE, CONFIRM, CONFIRM_DELETE_POST, MESSAGE_DELETE_COMPLETED } from '@/constants/title';
// store
import { usePopupStore } from '@/stores/popup';
import { useToastStore } from '@/stores/toast';
// util
import { handleLinkClick } from '@/utils/directive/editorLinkOpenNewTab';

// -----variable-----
const route = useRoute();
const router = useRouter();
const popupStore = usePopupStore();
const toastStore = useToastStore();

const commentData = ref<CommentWithReplies[]>([]);
const commentCntData = ref(0);
const postData = ref();
const myLike = ref();
const contentBody = ref<HTMLDivElement | null>(null);
// -----api-----
const { data: getKnowhowDetailData } = useGetKnowhowDetailApi({
	pk: Number(route.params.pk),
});
const { mutateAsync: deleteKnowhow } = useDeleteKnowhowApi();
const { mutateAsync: adjustLikes } = useAdjustLikesApi();
// -----function-----
const onClickLike = async (pk: number) => {
	const params = {
		knowhow_pk: pk,
	};
	const res = await adjustLikes(params);
	myLike.value = res.data.data.my_like;
};
const onClickDeleteKnowhow = async (pk: number) => {
	popupStore.openPopup({
		content: COMPANY_TITLE,
		subtext: CONFIRM_DELETE_POST,
		confirm: async () => {
			try {
				const params = {
					pk: pk,
				};
				await deleteKnowhow(params);

				toastStore.openToast({
					subContent: COMPANY_TITLE,
					content: MESSAGE_DELETE_COMPLETED,
				});
			} catch (error) {
				console.log(error);
			}
		},
		confirmText: CONFIRM,
	});
};
const onClickEditKnowhow = async () => {
	postData.value = {
		title: getKnowhowDetailData?.value?.knowhow.knowhow_title,
		content: getKnowhowDetailData?.value?.knowhow.knowhow_content,
		filePath: getKnowhowDetailData?.value?.knowhow.file_path,
		filePk: getKnowhowDetailData?.value?.knowhow.file_pk,
		knowhowPk: getKnowhowDetailData?.value?.knowhow.knowhow_pk,
	};

	router.replace({ query: { modal: 'open' } });
};

watch(
	() => getKnowhowDetailData.value,
	() => {
		myLike.value = getKnowhowDetailData.value?.my_like;
	}
);
//editor 링크 클릭 시 새창 열기
// COMMENT : AOS에서는 target: _blank로 새창을 띄울 수 없어 추가된 코드입니다.
watch(
	[getKnowhowDetailData],
	() => {
		contentBody.value = document.querySelector('.c_editorContent');

		if (contentBody.value) {
			// 기존 리스너 제거
			contentBody.value.removeEventListener('click', handleLinkClick);

			// 새로운 리스너 추가
			contentBody.value.addEventListener('click', handleLinkClick);
		}
	},
	{ immediate: true }
);
</script>

<style scoped lang="scss">
.container {
	display: flex;
	flex-direction: column;
	max-width: 68rem;
	padding-top: var(--header-height);
	margin: 7.2rem auto 8.8rem;
	@media screen and (max-width: 1280px) {
		// padding-bottom: var(--bottomMenu-height);
		margin: 4rem auto 8rem;
	}
}

.section_header {
	.header_container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		padding-bottom: 1.6rem;
		border-bottom: 1px solid #393d45;
		margin-bottom: 4rem;
		.header_title {
			word-break: break-all;
			font-size: var(--font-s-x-large);
			font-weight: var(--font-w-bold);
			line-height: 2.387rem;
		}
		.header_sub {
			display: flex;
			justify-content: space-between;
			font-size: var(--font-s-small);
			font-weight: var(--font-w-light);
			line-height: 1.551rem;
			color: #818393;
			.sub_info {
				display: flex;
				align-items: center;
				span {
					margin: 0 8px;
					position: relative;
				}
				span:first-child {
					margin-left: 0;
				}
				span:last-child::after {
					display: none; /* 마지막 span에는 border를 추가하지 않음 */
				}
				span::after {
					content: '';
					position: absolute;
					right: -8px;
					top: 50%;
					transform: translateY(-50%);
					height: 8px; /* border의 높이 */
					border-left: 1px solid #393d45; /* 원하는 border 색상으로 변경 */
				}
			}
			.sub_likeBtn {
				width: 2.4rem;
				height: 2.4rem;
				cursor: pointer;
			}
		}
	}
}

.section_body {
	display: flex;
	flex-direction: column;
	gap: 2.4rem;
	margin-bottom: 8.8rem;
	.body_box {
		background-color: var(--color-bg-mid);
		border-radius: 10px;
		.box_content {
			padding: 2rem;
			div {
				word-break: break-all;
			}
		}
	}
	.body_btn {
		display: flex;
		justify-content: end;
		gap: 0.8rem;
		button {
			font-size: var(--font-s-mid);
			font-weight: var(--font-w-semi-bold);
			line-height: 1.79rem;
			padding: 1.3rem 1.6rem;
			border-radius: 0.6rem;
		}
		.btn_delete {
			border: 1px solid #818393;
		}
		.btn_modify {
			background-color: #ff5e00;
		}
	}
}
</style>
